import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiBell, FiCheckCircle, FiExternalLink, FiFilter, FiX } from "react-icons/fi";
import { Alert, Button, EmptyState, StatusBadge } from "@swot/base-components";
import { normalizeUserSafeApiError } from "@cmts/api/apiErrorNormalizer.js";

export type CmtsNotificationItem = Record<string, unknown> & {
  id?: string | undefined;
  notificationId?: string | undefined;
  title?: string | undefined;
  message?: string | undefined;
  body?: string | undefined;
  priority?: string | undefined;
  status?: string | undefined;
  channel?: string | undefined;
  complaintId?: string | undefined;
  linkedEntityId?: string | undefined;
  linkedEntityType?: string | undefined;
  createdAt?: string | undefined;
  readAt?: string | undefined;
};

function persistedNotificationId(row: CmtsNotificationItem): string { return String(row.id ?? row.notificationId ?? "").trim(); }
function notificationRenderKey(row: CmtsNotificationItem): string { return persistedNotificationId(row); }
const CMTS_NOTIFICATION_TERMINAL_READ_STATUSES = new Set(["read", "acknowledged", "archived", "sent"]);
function isUnread(row: CmtsNotificationItem): boolean {
  const record = row as CmtsNotificationItem & { notificationState?: string; readState?: string };
  const state = String(record.notificationState ?? record.readState ?? row.status ?? "").toLowerCase();
  return !row.readAt && !CMTS_NOTIFICATION_TERMINAL_READ_STATUSES.has(state);
}
function notificationRoute(row: CmtsNotificationItem): string {
  const complaintId = String(row.complaintId ?? row.linkedEntityId ?? "");
  const type = String(row.linkedEntityType ?? "complaint").toLowerCase();
  if (complaintId && type.includes("workflow")) return `/cmts/complaints/${encodeURIComponent(complaintId)}/workflow`;
  if (complaintId && type.includes("evidence")) return `/cmts/complaints/${encodeURIComponent(complaintId)}/evidence`;
  if (complaintId) return `/cmts/complaints/${encodeURIComponent(complaintId)}`;
  if (type.includes("task")) return "/cmts/tasks";
  if (type.includes("escalation")) return "/cmts/escalations";
  return "/cmts/notifications";
}

export function CmtsNotificationDrawer(props: {
  open: boolean;
  notifications: readonly CmtsNotificationItem[];
  onClose: () => void;
  onNavigate: (path: string) => void;
  onMarkRead: (notificationId: string) => void | Promise<void>;
  onMarkAllRead: () => void | Promise<void>;
}) {
  const [filter, setFilter] = useState<"all" | "unread" | "critical">("all");
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const drawerRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!props.open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === "Escape") { event.preventDefault(); props.onClose(); } };
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (target && drawerRef.current && !drawerRef.current.contains(target)) props.onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown, true);
    };
  }, [props.open, props.onClose]);

  const rows = useMemo(() => props.notifications.filter((row) => {
    if (filter === "unread") return isUnread(row);
    if (filter === "critical") return String(row.priority ?? "").toLowerCase().includes("critical") || String(row.priority ?? "").toLowerCase().includes("high");
    return true;
  }), [props.notifications, filter]);

  async function runReadAction(actionId: string, action: () => void | Promise<void>): Promise<void> {
    try {
      setActionError(null);
      setBusyAction(actionId);
      await action();
    } catch (error) {
      setActionError(normalizeUserSafeApiError(error).userMessage || "Notification action failed.");
      throw error;
    } finally {
      setBusyAction(null);
    }
  }
  async function markAllNotificationsRead(): Promise<void> {
    await runReadAction("all", props.onMarkAllRead);
  }
  async function markReadOnly(id: string): Promise<void> {
    if (!id) {
      setActionError("Notification is missing a persisted id and cannot be marked read.");
      return;
    }
    await runReadAction(id, () => props.onMarkRead(id));
  }
  async function markReadAndNavigate(id: string, route: string): Promise<void> {
    if (!id) {
      setActionError("Notification is missing a persisted id and cannot be marked read.");
      return;
    }
    await runReadAction(id, () => props.onMarkRead(id));
    props.onNavigate(route);
    props.onClose();
  }

  if (!props.open || typeof document === "undefined") return null;
  return createPortal(
    <div className="cmts-drawer-backdrop cmts-notification-popover-root" role="presentation" data-overlay-dismiss="notification-drawer" data-cmts-notification-popover="singleton">
      <aside ref={drawerRef} className="cmts-notification-drawer" role="dialog" aria-modal="true" aria-labelledby="cmts-notification-drawer-title" onPointerDown={(event) => event.stopPropagation()}>
        <header className="cmts-drawer-header">
          <div>
            <p className="cmts-section-eyebrow"><FiBell aria-hidden="true" /> Notification Center</p>
            <h2 id="cmts-notification-drawer-title">Operational notifications</h2>
            <span>{props.notifications.filter(isUnread).length} unread of {props.notifications.length}</span>
          </div>
          <Button label="Close" variant="ghost" aria-label="Close notifications" onClick={props.onClose} leftIcon={<FiX aria-hidden="true" />} />
        </header>
        <div className="cmts-drawer-toolbar">
          <span><FiFilter aria-hidden="true" /> Filter</span>
          <Button label="All" variant={filter === "all" ? "primary" : "secondary"} onClick={() => setFilter("all")} />
          <Button label="Unread" variant={filter === "unread" ? "primary" : "secondary"} onClick={() => setFilter("unread")} />
          <Button label="Critical" variant={filter === "critical" ? "primary" : "secondary"} onClick={() => setFilter("critical")} />
          <Button label="Mark all read" variant="secondary" disabled={busyAction !== null} onClick={markAllNotificationsRead} />
        </div>
        {actionError ? <Alert title="Notification action failed" tone="danger">{actionError}</Alert> : null}
        <div className="cmts-notification-list">
          {rows.map((row) => {
            const id = persistedNotificationId(row);
            const renderKey = notificationRenderKey(row);
            if (!renderKey) return null;
            const unread = isUnread(row);
            const route = notificationRoute(row);
            return (
              <article key={renderKey} className={unread ? "cmts-notification-card cmts-notification-card--unread" : "cmts-notification-card"}>
                <Button className="cmts-notification-card__main" variant="ghost" onClick={() => markReadAndNavigate(id, route)}>
                  <span className="cmts-notification-card__icon"><FiBell /></span>
                  <span>
                    <strong>{String(row.title ?? row.message ?? "CMTS notification")}</strong>
                    <small>{String(row.body ?? row.message ?? row.linkedEntityType ?? "Open linked context")}</small>
                    <em>{String(row.channel ?? "in-app")} • {String(row.createdAt ?? "now")}</em>
                    <StatusBadge label={String(row.priority ?? "normal")} tone={String(row.priority ?? "").toLowerCase().includes("critical") || String(row.priority ?? "").toLowerCase().includes("high") ? "danger" : "info"} />
                  </span>
                </Button>
                <div className="cmts-notification-card__actions">
                  <Button label="Open" title="Open context" variant="ghost" leftIcon={<FiExternalLink aria-hidden="true" />} onClick={() => markReadAndNavigate(id, route)} />
                  <Button label="Read" title="Mark read" variant="ghost" leftIcon={<FiCheckCircle aria-hidden="true" />} disabled={busyAction !== null} onClick={() => markReadOnly(id)} />
                </div>
              </article>
            );
          })}
          {rows.length === 0 ? <EmptyState title="No notifications" message="No notifications match this filter." /> : null}
        </div>
      </aside>
    </div>,
    document.body,
  );
}
